import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect } from "react";
import { type VariableDefinition, useUnavailableVariableIds } from "@babackapp/variables-registry";
import PanelContent from "./panel-content";
import { createVariablesMentionExtension, parseMentionsFromText } from "../mention-extension";


interface PanelVariableInputContentProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  variables: VariableDefinition[];
  id?: string;
  placeholder?: string;
  outputFormat?: "html" | "text";
  triggerOnChange?: boolean;
}

export function PanelVariableInputContent({
  title,
  value,
  onChange,
  variables,
  id,
  placeholder,
  outputFormat = "text",
  triggerOnChange = false,
}: PanelVariableInputContentProps) {
  const unavailableVariableIds = useUnavailableVariableIds();
  const editor = useEditor({
    extensions: [
      Document,
      createVariablesMentionExtension(variables, unavailableVariableIds),
      Paragraph,
      Text,
    ],
    content:
      outputFormat === "html"
        ? value
        : parseMentionsFromText(value ?? "", variables),
    editorProps: {
      attributes: {
        class:
          "min-h-9 px-3 py-2 text-sm rounded-md border bg-background outline-none ring-0 focus:outline-none focus:ring-0",
        style: "white-space: nowrap; overflow-x: auto;",
        ...(placeholder && { placeholder }),
      },
      handleKeyDown: (view, event) => {
        // Prevent Enter key to make it single-line
        if (view && event.key === "Enter") {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
    onUpdate: triggerOnChange
      ? ({ editor }) => {
          const content =
            outputFormat === "html" ? editor.getHTML() : editor.getText();
          onChange(content);
        }
      : undefined,
  });

  useEffect(() => {
    if (!editor) return;
    const doc =
      outputFormat === "html"
        ? value
        : parseMentionsFromText(value ?? "", variables);
    if (
      editor.getJSON &&
      JSON.stringify(editor.getJSON()) !== JSON.stringify(doc)
    ) {
      editor.commands.setContent(doc, { emitUpdate: false });
    }
  }, [editor, value, variables, outputFormat]);

  const handleBlur = () => {
    if (!editor) return;
    const content =
      outputFormat === "html" ? editor.getHTML() : editor.getText();
    onChange(content);
  };

  return (
    <PanelContent title={title}>
      <EditorContent
        editor={editor}
        id={id}
        className="w-full"
        {...(!triggerOnChange && { onBlur: handleBlur })}
      />
    </PanelContent>
  );
}
